import { Button, ButtonGroup, Checkbox, Radio, RadioGroup, Stack, Text, Textarea } from '@chakra-ui/react';
import React, { ReactElement, useState } from 'react';
import { ApplicantStatusReason, IApplyUniConfig } from 'types';
import { CustomFormControl, Modal, Select } from 'shared-components';
import { statusReasonOptions } from 'data';
import { useSelector } from 'react-redux';
import { ApplicationState } from '@/store';
import { useTranslation } from "react-i18next";

type SubFlow = 'reRequest' | 'withdraw';

interface RequirementNotSatisfiedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReRequest: (description: string, internalNote: string) => void;
  onWithdraw: (reason: ApplicantStatusReason, explanation: string, note: string, notify: boolean) => void;
  defaultDescription?: string;
}

export const RequirementNotSatisfiedModal = ({
  isOpen,
  onClose,
  onReRequest,
  onWithdraw,
  defaultDescription = '',
}: RequirementNotSatisfiedModalProps): ReactElement => {
    const { t } = useTranslation('common');
  const [subFlow, setSubFlow] = useState<SubFlow>('reRequest');
  const [description, setDescription] = useState(defaultDescription);
  const [internalNote, setInternalNote] = useState('');
  const [statusReason, setStatusReason] = useState<ApplicantStatusReason | ''>('');
  const [withdrawNote, setWithdrawNote] = useState('');
  const [withdrawExplanationToApplicant, setWithdrawExplanationToApplicant] = useState('');
  const [notifyApplicant, setNotifyApplicant] = useState(false);
  const { applyConfig } = useSelector((state: ApplicationState) => state.applyConfig);

  const getDefaultExplanationToApplicant = (reason: ApplicantStatusReason | ''): string => {
    if (!reason) {
      return '';
    }

    const reasonType = (reason.charAt(0).toLowerCase() + reason.slice(1)) as keyof NonNullable<
      IApplyUniConfig['rejectionReasons']
    >;

    return applyConfig.rejectionReasons?.[reasonType] ?? '';
  };

  const handleConfirm = (): void => {
    if (subFlow === 'reRequest') {
      onReRequest(description, internalNote);
    } else if (subFlow === 'withdraw' && statusReason) {
      onWithdraw(statusReason, withdrawExplanationToApplicant, withdrawNote, notifyApplicant);
    }
    resetState();
  };

  const resetState = (): void => {
    setSubFlow('reRequest');
    setDescription(defaultDescription);
    setInternalNote('');
    setStatusReason('');
    setWithdrawNote('');
    setWithdrawExplanationToApplicant('');
    setNotifyApplicant(false);
  };

  const handleClose = (): void => {
    resetState();
    onClose();
  };

  const isConfirmDisabled = subFlow === 'withdraw' && (!statusReason || String(statusReason) === '');

  const confirmLabel = subFlow === 'reRequest' ? 'Re-request task' : 'Withdraw offer';
  const confirmColorScheme = subFlow === 'reRequest' ? 'blue' : 'red';

  return (
    <Modal
      show={isOpen}
      onClose={handleClose}
      title={t('common:requirementNotSatisfied')}
      footer={
        <ButtonGroup spacing={3} size="lg">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="solid"
            colorScheme={confirmColorScheme}
            isDisabled={isConfirmDisabled}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </ButtonGroup>
      }
    >
      <Stack spacing={4}>
        <Text fontWeight="medium" mb={0}>
          {t('common:howDoYouWantToProceed')}
                          </Text>

        <RadioGroup value={subFlow} onChange={(val: string) => setSubFlow(val as SubFlow)}>
          <Stack spacing={2}>
            <Radio value="reRequest" variant="outline">
              {t('common:reRequestTask')}
                                      </Radio>
            <Radio value="withdraw" variant="outline">
              {t('common:withdrawTheEntireOffer')}
                                      </Radio>
          </Stack>
        </RadioGroup>

        {subFlow === 'reRequest' && (
          <Stack spacing={4}>
            <CustomFormControl label={t('common:description')} subLabel={t('common:sharedWithTheApplicant')} size="lg">
              <Textarea size="lg" placeholder="" value={description} onChange={e => setDescription(e.target.value)} />
            </CustomFormControl>

            <CustomFormControl
              label={t('common:addAnInternalNoteOptional')}
              subLabel={t('common:notSharedWithTheApplicant')}
              size="lg"
            >
              <Textarea
                size="lg"
                placeholder={t('common:addNote')}
                value={internalNote}
                onChange={e => setInternalNote(e.target.value)}
              />
            </CustomFormControl>
          </Stack>
        )}

        {subFlow === 'withdraw' && (
          <Stack spacing={4}>
            <Select
              id="withdraw-reason"
              key="withdraw-reason"
              options={statusReasonOptions}
              value={statusReason}
              placeHolder={t('common:chooseReason')}
              label={t('common:reason')}
              onChange={(evt: React.FormEvent<HTMLSelectElement>) => {
                const reason = evt.currentTarget.value as ApplicantStatusReason;
                setStatusReason(reason);
                setWithdrawExplanationToApplicant(getDefaultExplanationToApplicant(reason));
              }}
              size="lg"
            />

            <CustomFormControl
              id="withdraw-explanation-to-applicant"
              label={t('common:explanationToApplicant2')}
              labelProps={{ htmlFor: 'withdraw-explanation-to-applicant' }}
              size="lg"
            >
              <Textarea
                id="withdraw-explanation-to-applicant"
                size="lg"
                placeholder=""
                value={withdrawExplanationToApplicant}
                onChange={e => setWithdrawExplanationToApplicant(e.target.value)}
                data-selector="withdraw-explanation-to-applicant-textarea"
              />
            </CustomFormControl>

            <CustomFormControl
              id="withdraw-note"
              label={t('common:internalNote2')}
              labelProps={{ htmlFor: 'withdraw-note' }}
              subLabel={t('common:thisWillNeverBeShownToTheApplica')}
              size="lg"
            >
              <Textarea
                id="withdraw-note"
                size="lg"
                placeholder={t('common:addNote')}
                value={withdrawNote}
                onChange={e => setWithdrawNote(e.target.value)}
              />
            </CustomFormControl>

            <Checkbox isChecked={notifyApplicant} onChange={e => setNotifyApplicant(e.target.checked)} size="lg">
              {t('common:notifyApplicantByEmail')}
                                      </Checkbox>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};
